/**
 * Маппер GET `admin/tariffs` → TariffsBundle. Деньги BE держит в тыйынах
 * (priceTiyin/overPriceTiyin) — UI работает в целых сомах. Линейка может
 * быть null (ещё не заведена на BE) — тогда version 0 («не задан», карточка
 * показывает это честно), а цены из мока служат черновиком для редактора.
 */

import {formatNumber} from '@utils/format';

import {CURRENT_CLINIC, CURRENT_SOLO} from '../mocks';
import type {ClinicTariff, SoloTariff, TariffHistoryItem, TariffsBundle} from '../types';

type Raw = Record<string, unknown>;

const asObj = (v: unknown): Raw | null => (typeof v === 'object' && v !== null ? (v as Raw) : null);
const asNum = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const asStr = (v: unknown): string => (typeof v === 'string' ? v : '');
const toSom = (tiyin: unknown): number => Math.round(asNum(tiyin) / 100);

const toSolo = (v: unknown): SoloTariff => {
  const r = asObj(v);
  if (r === null) return {...CURRENT_SOLO, version: 0, updatedAt: ''};
  return {
    version: asNum(r.version),
    updatedAt: asStr(r.updatedAt),
    price: toSom(r.priceTiyin),
  };
};

const toClinic = (v: unknown): ClinicTariff => {
  const r = asObj(v);
  if (r === null)
    return {
      ...CURRENT_CLINIC,
      version: 0,
      updatedAt: '',
      packages: CURRENT_CLINIC.packages.map((p) => ({...p})),
    };
  const packages = (Array.isArray(r.packages) ? r.packages : [])
    .map(asObj)
    .filter((p): p is Raw => p !== null)
    .map((p) => ({name: asStr(p.name), upTo: asNum(p.upTo), price: toSom(p.priceTiyin)}));
  return {
    version: asNum(r.version),
    updatedAt: asStr(r.updatedAt),
    packages,
    overPrice: toSom(r.overPriceTiyin),
  };
};

/**
 * ВРЕМЕННО (v1): BE собирает summary в тыйынах («до 3: 300000», «120000/мес»).
 * Конвертим цены в сомы на фронте; убрать, когда бэк начнёт отдавать сомы.
 * Защита от двойного деления: строка с пробел-группировкой цифр («3 000»)
 * считается уже отформатированной и не трогается.
 */
const normalizeSummary = (s: string): string => {
  if (/\d\s\d/.test(s)) return s;
  return s.replace(
    /(:\s*)(\d+)|(\d+)(\s*\/\s*(?:мес|место))/g,
    (
      match,
      colon: string | undefined,
      afterColon: string | undefined,
      before: string | undefined,
      unit: string | undefined,
    ) => {
      const num = Number(afterColon ?? before);
      if (num < 100 || num % 100 !== 0) return match;
      const som = formatNumber(num / 100);
      return colon === undefined ? `${som}${unit}` : `${colon}${som}`;
    },
  );
};

const toHistoryItem = (v: unknown): TariffHistoryItem | null => {
  const r = asObj(v);
  if (r === null) return null;
  return {
    id: asStr(r.id),
    kind: r.kind === 'solo' ? 'solo' : 'clinic',
    version: asNum(r.version),
    date: asStr(r.date),
    current: r.current === true,
    summary: normalizeSummary(asStr(r.summary)),
  };
};

export const toTariffsBundle = (raw: Raw): TariffsBundle => ({
  solo: toSolo(raw.solo),
  clinic: toClinic(raw.clinic),
  history: (Array.isArray(raw.history) ? raw.history : [])
    .map(toHistoryItem)
    .filter((h): h is TariffHistoryItem => h !== null),
});
